import React from 'react';
import ManualFootageControl from '../content/ManualFootageControl';
import {ContentReviewItem} from '../../services/contentReviewService';
import {getManualFootageOwner,manualFootageOwnerCurrent,manualFootageEnabled,readPendingFootage,clearPendingFootage,savePendingFootage,PendingFootageRequest} from '../../services/manualFootageService';

/** Reuse the reviewed journal/control on the current Socials review surface. */
export function useManualFootageReview(userId:string|undefined,items:ContentReviewItem[],refresh:()=>void|Promise<unknown>) {
 const owner=React.useMemo(()=>getManualFootageOwner(userId),[userId]);
 const [pending,setPending]=React.useState<PendingFootageRequest[]>([]);
 const [unknown,setUnknown]=React.useState(true);
 const [resolved,setResolved]=React.useState<Record<string,string>>({});
 const [locks,setLocks]=React.useState<Record<string,boolean>>({});
 React.useLayoutEffect(()=>{
  try {
   if(!manualFootageOwnerCurrent(owner)){setUnknown(true);setPending([]);return;}
   for(const saved of readPendingFootage(owner)) {
    const item=items.find(i=>i.id===saved.packageId);if(!item)continue;
    const command=item.command;const matching=command?.request_id===saved.request.request_id;
    const superseded=(item.fingerprint&&item.fingerprint!==saved.request.fingerprint)||(command&&!matching&&command.request_id!==saved.previousRequestId)||['P','H'].includes(item.verdict);
    if(superseded||(matching&&command&&(['refused','exhausted','conflict'].includes(command.state)||(command.state==='applied'&&command.child_ready===true))))clearPendingFootage(saved.packageId,saved.request.request_id,owner);
   }
   setPending(readPendingFootage(owner));setUnknown(false);
  }catch{setUnknown(true);}
 },[items,owner]);
 const blocked=(item:ContentReviewItem)=> Boolean(locks[item.id]||pending.some(p=>p.packageId===item.id)||(manualFootageEnabled()&&unknown));
 const remember=(saved:PendingFootageRequest)=>{if(manualFootageOwnerCurrent(owner))setPending(p=>[...p.filter(x=>x.packageId!==saved.packageId),saved]);};
 const render=(item:ContentReviewItem,seek:(seconds:number)=>void,reviewBusy=false)=>manualFootageEnabled()||pending.some(p=>p.packageId===item.id)?
  <ManualFootageControl key={JSON.stringify([item.id,item.fingerprint,owner,resolved[item.id]])} item={item} owner={owner}
   blocked={reviewBusy||Boolean(item.command&&['queued','leased','retry','applied'].includes(item.command.state))}
   onSeek={seek} onPending={remember} onLock={locked=>setLocks(p=>p[item.id]===locked?p:{...p,[item.id]:locked})}
   onConfirmed={async(saved,state)=>{
    if(!manualFootageOwnerCurrent(owner))return;
    try{
     // Terminal replay resolves only this identity; never journal a terminal state.
     // Conflict normally arrives as HTTP 409; retain defensive terminal handling.
     if(['refused','exhausted','conflict'].includes(state)){
      const matching=readPendingFootage(owner).some(row=>row.packageId===saved.packageId && row.request.request_id===saved.request.request_id);
      clearPendingFootage(saved.packageId,saved.request.request_id,owner);
      if(matching){
       setLocks(previous=>({...previous,[saved.packageId]:false}));
       // Reset the control's resolved local retry body even if list refresh fails.
       setResolved(previous=>({...previous,[saved.packageId]:saved.request.request_id}));
      }
     }
     else savePendingFootage({...saved,confirmedState:state as PendingFootageRequest['confirmedState']},owner);
     setPending(readPendingFootage(owner));setUnknown(false);
    }catch{setUnknown(true);}
    // A failed list refresh must not undo an acknowledged terminal resolution.
    try{await refresh();}catch{/* Existing list remains visible for a human retry. */}
   }}
   onRefresh={refresh}/>:null;
 return {blocked,render};
}
